'use client';

import { Accordion } from '@base-ui/react/accordion';
import clsx from 'clsx';
import { lazy, type ReactNode, Suspense, useState } from 'react';

import { SDSChevronIcon } from '@repo/sds/components/SDSChevronIcon';
import { SDSText, type SDSTextType } from '@repo/sds/components/SDSText';

import '@repo/sds/components/SDSAccordion/SDSAccordion.scss';

// Markdown rendering (markdown-to-jsx) loads only for accordions with string content, so link-list
// accordions such as the site footer don't ship it on every page.
const SDSRichText = lazy(() =>
  import('@repo/sds/components/SDSRichText').then(module => ({ default: module.SDSRichText })),
);

export interface SDSAccordionItem {
  id: string;
  title: string;
  /** HTML string (via SDSRichText) or custom React content (e.g. footer link lists). */
  content: ReactNode;
}

export interface SDSAccordionProps {
  items: SDSAccordionItem[];
  className?: string;
  iconSize?: number;
  /** Text style of the item titles. Without it titles use button-x-large, stepped up on mobile. */
  titleType?: SDSTextType;
  defaultExpanded?: string;
  collapsible?: boolean;
  itemsPerRow?: number;
  showDivider?: boolean;
  dataTestId?: string;
  itemTriggerTestIdPrefix?: string;
  value?: string[];
  onValueChange?: (value: string[]) => void;
}

export function SDSAccordion({
  items,
  className,
  defaultExpanded,
  iconSize = 16,
  titleType,
  itemsPerRow = 1,
  showDivider = false,
  dataTestId,
  itemTriggerTestIdPrefix,
  value,
  onValueChange,
}: SDSAccordionProps) {
  const defaultValue = defaultExpanded ? [defaultExpanded] : [];
  const [internalValue, setInternalValue] = useState<string[]>(defaultValue);
  const multipleValue = value ?? internalValue;

  const handleValueChange = (newValue: string | string[] | null) => {
    const next = newValue == null ? [] : Array.isArray(newValue) ? newValue : [newValue];
    if (value === undefined) {
      setInternalValue(next);
    }
    onValueChange?.(next);
  };

  if (!(items && Array.isArray(items)) || items.length === 0) {
    return null;
  }

  // Group items into columns for independent vertical stacking
  const getColumnGroups = () => {
    if (itemsPerRow <= 1) {
      return [items]; // Single column
    }
    // Split items into columns (e.g., with 6 items and 2 columns: column1=[0,2,4], column2=[1,3,5])
    const columns: SDSAccordionItem[][] = Array.from({ length: itemsPerRow }, () => []);
    items.forEach((item, index) => {
      const columnIndex = index % itemsPerRow;
      columns[columnIndex].push(item);
    });
    return columns;
  };

  const columnGroups = getColumnGroups();
  const multipleValueSet = new Set(multipleValue);

  return (
    <div
      className={clsx('SDSAccordion', { 'SDSAccordion--grid': itemsPerRow > 1 }, className)}
      data-testid={dataTestId}
    >
      <Accordion.Root
        className={clsx('SDSAccordion__root', { 'SDSAccordion__root--columns': itemsPerRow > 1 })}
        multiple
        value={multipleValue}
        onValueChange={handleValueChange}
      >
        {columnGroups.map(column => (
          <div key={`column-${column[0]?.id ?? 'empty'}`} className="SDSAccordion__column">
            {column.map(item => {
              const itemId = item.id;
              const isExpanded = multipleValueSet.has(itemId);
              const isLastItem = items[items.length - 1].id === item.id;

              return (
                <div key={itemId} className="SDSAccordion__item-wrapper">
                  <Accordion.Item value={itemId} className="SDSAccordion__item">
                    <Accordion.Header className="SDSAccordion__header">
                      <Accordion.Trigger
                        className={clsx('SDSAccordion__trigger', isExpanded && 'SDSAccordion__trigger--expanded')}
                        data-testid={itemTriggerTestIdPrefix ? `${itemTriggerTestIdPrefix}-${item.id}` : undefined}
                      >
                        <SDSText
                          text={item.title}
                          color="text-primary"
                          type={titleType ?? 'button-x-large'}
                          className={clsx(
                            'SDSAccordion__title',
                            !titleType && 'SDSAccordion__title--default-type',
                            isExpanded && 'SDSAccordion__title--expanded',
                          )}
                        />
                        <SDSChevronIcon className="SDSAccordion__icon" size={iconSize} isExpanded={isExpanded} />
                      </Accordion.Trigger>
                    </Accordion.Header>
                    <Accordion.Panel className="SDSAccordion__panel">
                      <div className="SDSAccordion__content">
                        {typeof item.content === 'string' ? (
                          <Suspense fallback={null}>
                            <SDSRichText content={item.content} />
                          </Suspense>
                        ) : (
                          item.content
                        )}
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
                  {showDivider && !isLastItem && <div className="SDSAccordion__divider" />}
                </div>
              );
            })}
          </div>
        ))}
      </Accordion.Root>
    </div>
  );
}
